import { useMemo, useState, type ReactNode } from "react";
import { useParams } from "react-router-dom";
import CommandLayout from "../../components/war-room/CommandLayout";
import { useCaseTimeline, type TimelineEvent } from "../../hooks/useCaseTimeline";
import { useCaseWeb, type WebNode } from "../../hooks/useCaseWeb";
import { useToggleInclusion } from "../../hooks/useToggleInclusion";
import { useSoundTheHorn, useHornStatus } from "../../hooks/useSoundTheHorn";
import { useLex } from "../../lib/theme/ThemeProvider";

/**
 * The Horn — the case chronology, woven by the Norns. Two faces of one spine:
 *   - The Saga: linear chronology at three zoom levels (chapter / episode / message)
 *   - The Web of Wyrd: the fate-web of who is tied to whom (communication + accusation)
 * Each analysis phase repopulates berserkr.events; this reads fetch_case_timeline + fetch_case_web.
 */
function Horn() {
  const { caseId } = useParams<{ caseId: string }>();
  const lex = useLex();
  const [mode, setMode] = useState<"saga" | "web">("saga");
  const horn = useSoundTheHorn();
  const { data: hornStatus } = useHornStatus(caseId);

  return (
    <CommandLayout caseId={caseId} variant="ctafinal" currentCommand="horn">
      <header className="rounded border border-steel-dark bg-iron/70 px-6 py-6 backdrop-blur-sm">
        <p className="mb-2 font-display text-base font-light uppercase tracking-[0.18em] text-bone-mute">
          {`// ${lex.commands.horn.verb.toLowerCase()}`}
        </p>
        <h2 className="font-display text-2xl font-light leading-tight tracking-tight text-bone md:text-3xl"
            style={{ textShadow: "0 2px 16px rgba(0,0,0,0.6)" }}>
          Every move, in order.
        </h2>
        <p className="mt-3 max-w-3xl font-display text-sm leading-relaxed text-bone-dim">
          One canonical chronology, woven from the corpus. Each analysis pass adds to the
          weave — the Saga reads it in order, the Web of Wyrd shows who is bound to whom.
        </p>

        <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
          <div className="inline-flex rounded border border-steel-dark/80 bg-black/30 p-0.5 font-display text-base font-light uppercase tracking-[0.14em]">
            <ModeTab label={lex.ui.sagaTab} active={mode === "saga"} onClick={() => setMode("saga")} />
            <ModeTab label={lex.ui.webTab} active={mode === "web"} onClick={() => setMode("web")} />
          </div>
          <div className="flex items-center gap-3">
            {hornStatus?.last_sounded && (
              <span className="font-display text-sm font-light uppercase tracking-[0.1em] text-bone-mute/70">
                {lex.commands.horn.lastLabel} {new Date(hornStatus.last_sounded).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                {hornStatus.new_evidence_since > 0 ? ` · ${hornStatus.new_evidence_since} new since` : ""}
              </span>
            )}
            <button
              type="button"
              disabled={!caseId || horn.isPending}
              onClick={() => caseId && horn.mutate(caseId)}
              className={`rounded border px-3 py-1.5 font-display text-base font-light uppercase tracking-[0.14em] transition-colors ${
                horn.isPending
                  ? "border-ember-bright/40 bg-ember/10 text-ember-bright"
                  : (hornStatus?.new_evidence_since ?? 0) > 0
                    ? "border-ember-bright/70 bg-ember/20 text-ember-bright shadow-ember-soft animate-pulse-ember"
                    : "border-ember-bright/50 bg-ember/10 text-bone hover:bg-ember/30 hover:text-ember-bright"
              }`}
            >
              {horn.isPending ? lex.commands.horn.pending : `⊙ ${lex.commands.horn.action}`}
            </button>
          </div>
        </div>
        {horn.isError && (
          <p className="mt-2 font-display text-base text-ember-bright">// horn failed: {(horn.error as Error)?.message}</p>
        )}
        {horn.isSuccess && !horn.isPending && (
          <p className="mt-2 font-display text-sm font-light uppercase tracking-[0.1em] text-bone-mute/70">{`// ${lex.ui.timelineRewoven}`} · {horn.data?.episodes} episodes · {horn.data?.chapters} chapters</p>
        )}
      </header>

      {mode === "saga" ? <Saga caseId={caseId} /> : <WebOfWyrd caseId={caseId} />}
    </CommandLayout>
  );
}

function ModeTab({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`rounded px-3 py-1.5 transition-colors ${
        active ? "bg-ember-bright/20 text-ember-bright" : "text-bone-mute hover:text-bone-dim"
      }`}
    >
      {label}
    </button>
  );
}

/* ============================ THE SAGA ============================ */

type Zoom = "chapter" | "episode" | "message";

function Saga({ caseId }: { caseId: string | undefined }) {
  const lex = useLex();
  const { data, isLoading, error } = useCaseTimeline(caseId);
  const [zoom, setZoom] = useState<Zoom>("chapter");
  const toggle = useToggleInclusion();

  const byLevel = useMemo(() => {
    const ev = data?.events ?? [];
    return {
      chapter: ev.filter((e) => e.granularity === "chapter"),
      episode: ev.filter((e) => e.granularity === "episode"), // includes anchors, interleaved by date
      message: ev.filter((e) => e.granularity === "message"),
    };
  }, [data]);

  const anchors = useMemo(
    () => (data?.events ?? []).filter(isAnchor)
      .sort((a, b) => (a.occurred_at ?? "").localeCompare(b.occurred_at ?? "")),
    [data],
  );

  // chapter view: interleave the anchor milestones chronologically among the chapter cards
  const rows = useMemo(() => {
    if (zoom === "chapter") {
      return [...byLevel.chapter, ...anchors]
        .sort((a, b) => (a.occurred_at ?? "").localeCompare(b.occurred_at ?? ""));
    }
    return byLevel[zoom];
  }, [zoom, byLevel, anchors]);

  return (
    <div className="rounded border border-steel-dark bg-iron/70 backdrop-blur-sm">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-steel-dark/70 px-5 py-3">
        <span className="font-display text-base font-light uppercase tracking-[0.16em] text-bone-dim">chronology</span>
        <div className="inline-flex rounded border border-steel-dark/80 bg-black/30 p-0.5 font-display text-sm font-light uppercase tracking-[0.2em]">
          {(["chapter", "episode", "message"] as Zoom[]).map((z) => (
            <button key={z} onClick={() => setZoom(z)}
              className={`rounded px-2.5 py-1 transition-colors ${
                zoom === z ? "bg-blue-bright/20 text-blue-bright" : "text-bone-mute hover:text-bone-dim"
              }`}>
              {z}{byLevel[z].length ? ` (${byLevel[z].length})` : ""}
            </button>
          ))}
        </div>
      </div>

      <div className="px-6 py-7">
        {isLoading && <p className="font-display text-base text-bone-mute">{`// ${lex.ui.timelineWeaving}`}</p>}
        {error && <p className="font-display text-base text-ember-bright">// {error.message}</p>}
        {!isLoading && !error && rows.length === 0 && (
          <p className="font-display text-base text-bone-mute">// no {zoom}-level events yet</p>
        )}
        {!isLoading && rows.length > 0 && (
          <div className="relative">
            <div className="absolute left-[7px] top-2 bottom-2 w-px bg-steel-dark" />
            <ul className="space-y-6">
              {rows.map((e) => <SagaNode key={e.id} ev={e} zoom={zoom} onExclude={() => { if (caseId && e.item_id && e.item_kind) toggle.mutate({ caseId, itemKind: e.item_kind, itemId: e.item_id, included: false }); }} />)}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "undated";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "undated";
  return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

// Anchor facts: verified, sourced milestones inserted into the spine (build_stamp
// anchor/*). They carry no evidence/filing link and a details.source_kind. We
// surface them as their own always-visible rail rather than burying them among
// the email episodes.
function isAnchor(e: TimelineEvent): boolean {
  return e.event_type === "other" && !e.item_id && !!(e.details as { source_kind?: string } | undefined)?.source_kind;
}

function SagaNode({ ev, zoom, onExclude }: { ev: TimelineEvent; zoom: Zoom; onExclude: () => void }) {
  if (isAnchor(ev)) {
    const d = (ev.details ?? {}) as { label?: string; source?: string };
    return (
      <li className="relative pl-6">
        <span className="absolute left-0 top-1.5 inline-block h-3.5 w-3.5 rounded-full border-2 border-iron bg-ember-bright shadow-ember-soft" />
        <p className="font-display text-base font-light uppercase tracking-[0.14em] text-ember-bright/90">
          {fmtDate(ev.occurred_at)}<span className="ml-2 text-ember-bright/60">⚑ milestone</span>
        </p>
        <p className="mt-1 max-w-3xl font-display text-sm font-light leading-relaxed text-bone">{d.label ?? ev.description}</p>
        {d.source && (
          <p className="mt-0.5 font-display text-[11px] uppercase tracking-wider text-bone-mute/60">{d.source}</p>
        )}
      </li>
    );
  }

  const dot =
    ev.significance === "high" ? "bg-ember-bright shadow-ember-soft"
    : zoom === "chapter" ? "bg-blue-bright" : "bg-bone-mute";

  if (zoom === "chapter") {
    const span = ev.details?.start_date && ev.details?.end_date
      ? `${fmtDate(ev.details.start_date)} — ${fmtDate(ev.details.end_date)}`
      : fmtDate(ev.occurred_at);
    const members = Array.isArray(ev.details?.members) ? ev.details!.members!.length : 0;
    return (
      <li className="relative pl-6">
        <span className={`absolute left-0 top-1.5 inline-block h-3.5 w-3.5 rounded-full border-2 border-iron ${dot}`} />
        <p className="font-display text-base font-light uppercase tracking-[0.14em] text-bone-mute">{span}{members ? ` · ${members} threads` : ""}</p>
        <p className="mt-1 font-display text-lg font-light text-bone">{ev.details?.title ?? "Chapter"}</p>
        <p className="mt-1 max-w-3xl font-display text-sm leading-relaxed text-bone-dim">{ev.details?.summary ?? ev.description}</p>
      </li>
    );
  }

  return (
    <li className="relative pl-6">
      <span className={`absolute left-0 top-1.5 inline-block h-3 w-3 rounded-full border-2 border-iron ${dot}`} />
      <p className="font-display text-base font-light uppercase tracking-[0.14em] text-bone-mute">
        {fmtDate(ev.occurred_at)}
        <span className="ml-2 text-bone-mute/60">{ev.event_type.replace(/_/g, " ")}</span>
      </p>
      <p className={`mt-1 max-w-3xl font-display leading-relaxed text-bone-dim ${zoom === "episode" ? "text-sm" : "text-[13px]"}`}>
        {ev.description}
      </p>
      {ev.item_id && (
        <button type="button" onClick={onExclude}
          className="mt-1 font-display text-sm font-light uppercase tracking-[0.1em] text-bone-mute/50 transition-colors hover:text-ember-bright">
          ✕ exclude this piece
        </button>
      )}
    </li>
  );
}

/* ========================= WEB OF WYRD ========================= */

type Zone = "P" | "D" | "W" | "N" | "T";

const W = 900;
const H = 640;

const KIND_COLOR: Record<string, string> = {
  party: "#e8703a",      // ember
  counsel: "#5b9bd5",    // blue
  neutral: "#cbbfa6",    // bone
  third_party: "#7d8794",
  regulator: "#b06ad5",
  unknown: "#5a6470",
};

function zoneOf(n: WebNode): "P" | "D" | "W" | "N" | "T" {
  const r = (n.represents ?? "").toLowerCase();
  const k = (n.kind ?? "").toLowerCase();
  if (r === "plaintiff") return "P";
  if (r === "defendant" || r === "medicap") return "D";
  if (r === "widow") return "W";
  if (r === "estate" || r.startsWith("estate") || k === "neutral") return "N";
  return "T";
}

function WebOfWyrd({ caseId }: { caseId: string | undefined }) {
  const { data, isLoading, error } = useCaseWeb(caseId);
  const [hover, setHover] = useState<string | null>(null);

  const layout = useMemo(() => {
    const nodes = data?.nodes ?? [];
    const zones: Record<Zone, WebNode[]> = { P: [], D: [], W: [], N: [], T: [] };
    nodes.forEach((n) => zones[zoneOf(n)].push(n));

    const pos: Record<string, { x: number; y: number; n: WebNode }> = {};
    const place = (arr: WebNode[], cx: number, cyTop: number, cyBot: number) => {
      const m = arr.length;
      arr.forEach((n, i) => {
        const t = m === 1 ? 0.5 : i / (m - 1);
        pos[n.key] = { x: cx, y: cyTop + t * (cyBot - cyTop), n };
      });
    };
    place(zones.P, 165, 150, H - 150);          // plaintiff — left column
    place(zones.D, W - 165, 150, H - 150);       // defendant/medicap — right column
    place(zones.N, W / 2, 70, 70 + Math.max(0, zones.N.length - 1) * 64); // neutral/estate — top
    place(zones.W, W - 150, H - 90, H - 90 - Math.max(0, zones.W.length - 1) * 56); // widow — lower right
    place(zones.T, W / 2, H - 60, H - 60 - Math.max(0, zones.T.length - 1) * 52);   // third party — bottom

    // resolve a char-edge name to a node key by label match
    const byLabel = (name: string): string | null => {
      const q = name.trim().toLowerCase();
      if (!q || q === "third_party") return null;
      let best: string | null = null;
      for (const k in pos) {
        const pv = pos[k];
        if (!pv) continue;
        const lbl = (pv.n.label ?? "").toLowerCase();
        if (!lbl) continue;
        if (lbl === q) return k;
        const surn = q.split(/\s+/).pop()!;
        if (surn.length > 2 && lbl.includes(surn)) best = k;
      }
      return best;
    };
    return { pos, byLabel };
  }, [data]);

  if (isLoading) return <Panel><p className="font-display text-base text-bone-mute">// weaving the web…</p></Panel>;
  if (error) return <Panel><p className="font-display text-base text-ember-bright">// {error.message}</p></Panel>;

  const { pos, byLabel } = layout;
  const comm = data?.comm_edges ?? [];
  const chars = data?.char_edges ?? [];
  const connected = (k: string) =>
    !hover || hover === k ||
    comm.some((e) => (e.source === hover && e.target === k) || (e.target === hover && e.source === k)) ||
    chars.some((e) => { const s = byLabel(e.source), t = byLabel(e.target); return (s === hover && t === k) || (t === hover && s === k); });

  return (
    <Panel>
      <div className="mb-3 flex flex-wrap items-center gap-x-5 gap-y-2 font-display text-sm font-light uppercase tracking-[0.2em] text-bone-mute">
        <span className="flex items-center gap-1.5"><i className="inline-block h-2 w-2 rounded-full" style={{ background: KIND_COLOR.party }} /> party</span>
        <span className="flex items-center gap-1.5"><i className="inline-block h-2 w-2 rounded-full" style={{ background: KIND_COLOR.counsel }} /> counsel</span>
        <span className="flex items-center gap-1.5"><i className="inline-block h-2 w-2 rounded-full" style={{ background: KIND_COLOR.neutral }} /> neutral</span>
        <span className="flex items-center gap-1.5"><i className="inline-block h-2 w-2 rounded-full" style={{ background: KIND_COLOR.third_party }} /> third party</span>
        <span className="ml-auto flex items-center gap-1.5"><i className="inline-block h-px w-5" style={{ background: "#5b9bd5" }} /> communicates</span>
        <span className="flex items-center gap-1.5"><i className="inline-block h-px w-5" style={{ background: "#e8703a" }} /> accuses</span>
      </div>

      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ maxHeight: 640 }}>
        <defs>
          <marker id="arrow-ember" markerWidth="9" markerHeight="9" refX="8" refY="3" orient="auto" markerUnits="strokeWidth">
            <path d="M0,0 L8,3 L0,6 Z" fill="#e8703a" />
          </marker>
        </defs>

        {/* communication ties */}
        {comm.map((e, i) => {
          const a = pos[e.source], b = pos[e.target];
          if (!a || !b) return null;
          const lit = !hover || hover === e.source || hover === e.target;
          return <line key={`c${i}`} x1={a.x} y1={a.y} x2={b.x} y2={b.y}
            stroke="#5b9bd5" strokeWidth={Math.min(0.6 + e.weight * 0.5, 3)}
            strokeOpacity={lit ? 0.22 + Math.min(e.weight * 0.06, 0.3) : 0.04} className="transition-opacity" />;
        })}

        {/* accusation threads */}
        {chars.map((e, i) => {
          const s = byLabel(e.source), t = byLabel(e.target);
          if (!s || !t || !pos[s] || !pos[t]) return null;
          const a = pos[s], b = pos[t];
          const lit = !hover || hover === s || hover === t;
          const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2 - 38;
          return <path key={`a${i}`} d={`M${a.x},${a.y} Q${mx},${my} ${b.x},${b.y}`}
            fill="none" stroke="#e8703a" strokeWidth={e.defendant_relevant ? 1.6 + e.weight * 0.5 : 1}
            strokeOpacity={lit ? (e.defendant_relevant ? 0.9 : 0.5) : 0.08}
            strokeDasharray={e.defendant_relevant ? undefined : "3 3"}
            markerEnd="url(#arrow-ember)" className="transition-opacity" />;
        })}

        {/* nodes */}
        {Object.entries(pos).map(([k, p]) => {
          const color = KIND_COLOR[(p.n.kind ?? "unknown")] ?? KIND_COLOR.unknown;
          const lit = connected(k);
          const right = p.x > W / 2;
          return (
            <g key={k} opacity={lit ? 1 : 0.18} className="cursor-pointer transition-opacity"
               onMouseEnter={() => setHover(k)} onMouseLeave={() => setHover(null)}>
              <circle cx={p.x} cy={p.y} r={p.n.kind === "party" ? 8 : 6} fill={color}
                stroke="#0c0f12" strokeWidth={2} />
              <text x={right ? p.x - 13 : p.x + 13} y={p.y - 1} textAnchor={right ? "end" : "start"}
                fontSize={11} fill="#e7e2d6" fontFamily="ui-monospace, monospace">{p.n.label ?? k}</text>
              <text x={right ? p.x - 13 : p.x + 13} y={p.y + 11} textAnchor={right ? "end" : "start"}
                fontSize={8.5} fill="#8a93a0" fontFamily="ui-monospace, monospace" className="uppercase tracking-wider">
                {p.n.represents ?? p.n.kind ?? ""}
              </text>
            </g>
          );
        })}
      </svg>

      <p className="mt-3 font-display text-sm font-light uppercase tracking-[0.12em] text-bone-mute/60">
        hover a name to isolate their threads · solid ember = accusation against a defendant
      </p>
    </Panel>
  );
}

function Panel({ children }: { children: ReactNode }) {
  return (
    <div className="rounded border border-steel-dark bg-iron/70 px-6 py-6 backdrop-blur-sm">
      {children}
    </div>
  );
}

export default Horn;
